import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  useAccountSettings,
  useAuth,
  useBalances,
  useDebounce,
  useMerchantApiQuery,
  useMerchantApiQueryWithParams,
} from "../hooks";

import { IRemoteDataContext, IBank, NoMeta } from "../interfaces-and-types";
import { BanksListContext } from "./BanksListContext";
import { appConstants } from "../constants";

export function BanksListProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState<boolean>(true);
  const debouncePeriodElapsed = useDebounce(100, isRefreshing);
  const accountSettingsContext = useAccountSettings();
  const [country, setCountry] = useState<string | null>(
    appConstants.DEFAULT_COUNTRY_STATIC
  );
  const { activeBalanceCurrency } = useBalances();

  const { isLoading, isSuccess, isError, data } = useMerchantApiQueryWithParams<
    { country: string; currency: string },
    IBank[],
    NoMeta
  >(
    [
      "getBanks",
      {
        country: country || "",
        currency: activeBalanceCurrency || "",
      },
    ],
    {
      enabled: debouncePeriodElapsed && !!activeBalanceCurrency,
    }
  );

  useEffect(() => {
    if (isRefreshing) {
      queryClient.refetchQueries(["getBanksList"]);

      setIsRefreshing(false);
    }
  }, [isRefreshing]);

  const refreshBanksListByCountry = (country: string) => {
    setCountry(country);
    setIsRefreshing(true);
  };

  // const is_live = data?.is_live || false;

  const memoedBanksListContext = useMemo(() => {
    const unMemoedBanksListContext: IRemoteDataContext<IBank[]> = {
      isSuccess,
      isError,
      isLoading,
      data: data || [],
      refreshRemoteData: refreshBanksListByCountry,
    };

    return unMemoedBanksListContext;
  }, [isSuccess, isRefreshing, !!accountSettingsContext.data?.is_live]);

  return (
    <BanksListContext.Provider value={memoedBanksListContext}>
      {children}
    </BanksListContext.Provider>
  );
}
